import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowLeft, Calendar, MapPin } from 'lucide-react';
import SEO from '../../components/SEO';
import StructuredData from '../../components/StructuredData';
import LandingNavBar from '../../components/LandingNavBar';
import LandingFooter from '../../components/LandingFooter';

const SITE = 'https://staymii.com';
const PAGE = '/press/beds24-e-invoice-official-launch';
const DATE_ISO = '2026-10-23';
const IMAGE = '/og/staymii-beds24-official-launch.jpg';
const TITLE = 'StayMii\u2122 & Beds24 e-Invoice Integration Officially Launches in Malaysia';

const articleSchema = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: TITLE,
  description: 'The StayMii x Beds24 e-Invoice integration is officially live from 23 October 2026, automating LHDN MyInvois e-invoice generation and submission for Malaysian hotels.',
  image: SITE + IMAGE,
  datePublished: DATE_ISO,
  dateModified: DATE_ISO,
  mainEntityOfPage: SITE + PAGE,
  author: { '@type': 'Organization', name: 'StayMii', url: SITE },
  publisher: { '@type': 'Organization', name: 'StayMii', url: SITE, logo: { '@type': 'ImageObject', url: SITE + '/og-image.jpg' } },
  keywords: 'Beds24 e-Invoice Malaysia, Beds24 MyInvois Integration, LHDN e-Invoice for Beds24, Hotel e-Invoice Malaysia',
};

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: SITE },
    { '@type': 'ListItem', position: 2, name: 'Press', item: SITE + '/press' },
    { '@type': 'ListItem', position: 3, name: 'Beds24 e-Invoice Official Launch', item: SITE + PAGE },
  ],
};

const paras = [
  { h: 'FOR IMMEDIATE RELEASE', p: 'StayMii, the Malaysian hospitality e-invoicing and property-compliance platform by Mii Global Partner Holdings Sdn. Bhd., today announced the official launch of its Beds24 e-Invoice integration, live from 23 October 2026. Following a successful soft launch on 16 July 2026, the integration is now generally available to every Malaysian hotel, serviced apartment and short-term rental operator using Beds24.' },
  { h: 'Simpler. Smarter. LHDN Compliant.', p: 'Operators connect their Beds24 property management system to StayMii once, and every booking is automatically converted into a structured LHDN e-invoice and submitted to MyInvois \u2014 returned with an official validation UUID and QR code. No re-keying, no spreadsheets, no missed submissions.' },
  { h: 'What the integration delivers', p: 'Seamless integration with a direct Beds24 connection; automatic e-invoice generation and submission to LHDN MyInvois; less manual work and fewer errors; compliance with Malaysia\u2019s e-Invoice requirements including SST, service charge and tourism tax; and a reliable, efficient platform built specifically for hotels.' },
  { h: 'Proven during soft launch', p: 'Since July, early-access properties have used the integration in live operations, helping StayMii refine booking sync, Credit and Debit Note handling for cancellations and amendments, and month-end reconciliation against Beds24 reports.' },
  { h: 'Availability', p: 'The Beds24 e-Invoice integration is available now. Operators can start a 14-day free trial with no credit card required, or request a demo at staymii.com/integrations/beds24-e-invoice. Setup takes under 15 minutes with no migration and no IT team required.' },
  { h: 'About StayMii', p: 'StayMii is a Malaysia-based, MySTI-certified platform providing LHDN e-Invoice automation, property management, e-ticketing and compliance tools for hotels, homestays, attractions and short-term rental operators. Powering Malaysian Hospitality.' },
];

export default function Beds24OfficialLaunchPressRelease() {
  return (
    <div className="min-h-screen bg-white" data-testid="beds24-official-launch-page">
      <SEO
        title="StayMii & Beds24 e-Invoice Integration Officially Launches (23 October 2026)"
        description="The StayMii x Beds24 e-Invoice integration is officially live from 23 October 2026 — automatic LHDN MyInvois e-invoice generation and submission for Malaysian hotels."
        keywords="beds24 e-invoice malaysia, beds24 myinvois integration, lhdn e-invoice for beds24, hotel e-invoice malaysia, staymii press release"
        canonicalUrl={PAGE}
        ogImage={SITE + IMAGE}
      />
      <StructuredData id="beds24-official-launch" schema={[articleSchema, breadcrumbSchema]} />
      <LandingNavBar />

      <article className="max-w-3xl mx-auto px-6 pt-28 pb-20">
        <Link to="/press" className="text-sm text-blue-600 hover:underline inline-flex items-center gap-1 mb-6" data-testid="press-back-link"><ArrowLeft className="w-3.5 h-3.5" /> Press & News</Link>
        <div className="flex flex-wrap items-center gap-4 text-xs text-slate-500 mb-4">
          <span className="inline-flex items-center gap-1.5" data-testid="press-date"><Calendar className="w-3.5 h-3.5" /> 23 October 2026</span>
          <span className="inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> Kuala Lumpur, Malaysia</span>
          <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 font-semibold">Official Launch</span>
        </div>
        <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900 leading-tight" data-testid="press-title">{TITLE}</h1>
        <figure className="mt-8" data-testid="press-hero-image">
          <img src={IMAGE} alt="StayMii x Beds24 official e-Invoice integration partnership launch" className="w-full rounded-2xl border border-slate-200 shadow-sm" />
          <figcaption className="mt-3 text-center text-sm text-slate-500">StayMii &times; Beds24 — Official e-Invoice Integration Partnership</figcaption>
        </figure>
        <div className="mt-8 space-y-7">
          {paras.map((s, i) => (
            <div key={i}>
              <h2 className="text-base md:text-lg font-semibold text-slate-900">{s.h}</h2>
              <p className="text-slate-600 mt-2 leading-relaxed">{s.p}</p>
            </div>
          ))}
        </div>
        <p className="mt-8 text-sm text-slate-500">Read the earlier <Link to="/press/beds24-e-invoice-soft-launch" className="text-blue-600 hover:underline" data-testid="press-soft-launch-link">soft-launch announcement (16 July 2026)</Link>.</p>
        <div className="mt-10 p-6 rounded-2xl bg-slate-50 border border-slate-200 text-center">
          <p className="font-semibold text-slate-900">Get started with Beds24 e-Invoice</p>
          <div className="mt-4 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/auth/signup" data-testid="press-get-started" className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-blue-600 hover:bg-blue-500 text-white font-semibold">Get Started <ArrowRight className="w-4 h-4" /></Link>
            <Link to="/contact" data-testid="press-request-demo" className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-white text-slate-900 font-semibold border border-slate-300 hover:bg-slate-100">Request a Demo</Link>
          </div>
        </div>
      </article>

      <LandingFooter />
    </div>
  );
}
